import { createReducer } from '@reduxjs/toolkit';
import type {
  CategoryPage,
  CustomPage,
  StoryPage,
  TagPage,
} from '@vgtv/api-client/lib/page';
import type { CnpStoryId } from '@vgtv/api-client/lib/cnp_story';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { CaseReducer } from '@reduxjs/toolkit/src/createReducer';

import type { Vendor } from '../../types';

import {
  fetchPageByCategoryId,
  fetchPageByTagId,
  fetchPageByStoryId,
  fetchPageBySlug,
} from './actions';
import { mixInParticipantsSlider } from './utils';

export * from './selectors';

export type PagesState = Record<
  Vendor,
  {
    byCategoryId: Record<SvpCategoryId, CategoryPage>;
    byTagId: Record<CnpTagId, TagPage>;
    byStoryId: Record<CnpStoryId, StoryPage>;
    bySlug: Record<string, CustomPage>;
  }
>;

const initialState: PagesState = {
  vgtv: {
    byCategoryId: {},
    byTagId: {},
    byStoryId: {},
    bySlug: {},
  },
  ab: {
    byCategoryId: {},
    byTagId: {},
    byStoryId: {},
    bySlug: {},
  },
  brandstudio: {
    byCategoryId: {},
    byTagId: {},
    byStoryId: {},
    bySlug: {},
  },
};

export const storePageByCategoryId: CaseReducer<
  PagesState,
  Pick<ReturnType<typeof fetchPageByCategoryId.fulfilled>, 'type' | 'payload'>
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    mixInParticipantsSlider(pageConfig);

    state[pageConfig.provider].byCategoryId[pageConfig.category.id] =
      pageConfig;
  }
};

export const storePageByTagId: CaseReducer<
  PagesState,
  Pick<ReturnType<typeof fetchPageByTagId.fulfilled>, 'type' | 'payload'>
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    state[pageConfig.provider].byTagId[pageConfig.tag.id] = pageConfig;
  }
};

export const storePageByStoryId: CaseReducer<
  PagesState,
  Pick<ReturnType<typeof fetchPageByStoryId.fulfilled>, 'type' | 'payload'>
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    state[pageConfig.provider].byStoryId[pageConfig.story.id] = pageConfig;
  }
};

export const storePageBySlug: CaseReducer<
  PagesState,
  Pick<ReturnType<typeof fetchPageBySlug.fulfilled>, 'type' | 'payload'>
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    state[pageConfig.provider].bySlug[pageConfig.slug] = pageConfig;
  }
};

const pagesReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(fetchPageByCategoryId.fulfilled, storePageByCategoryId)
    .addCase(fetchPageByTagId.fulfilled, storePageByTagId)
    .addCase(fetchPageByStoryId.fulfilled, storePageByStoryId)
    .addCase(fetchPageBySlug.fulfilled, storePageBySlug)
);

export {
  pagesReducer,
  fetchPageByCategoryId,
  fetchPageByTagId,
  fetchPageByStoryId,
  fetchPageBySlug,
};
